// src/hooks/useClaude.js
import { useState, useCallback } from "react";

const CLAUDE_URL = `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/claude`;
const TIMEOUT_MS = 25000;

async function fetchWithTimeout(url, options, ms = TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

function getErrorMessage(error) {
  if (error.name === "AbortError") return {
    title: "Le Chroniqueur prend trop de temps",
    detail: "Le serveur met trop longtemps à répondre. Réessaie.",
    retryable: true,
  };
  if (error.message?.includes("Failed to fetch") || error.message?.includes("NetworkError")) return {
    title: "Le serveur démoniaque est hors ligne",
    detail: "Vérifie que le serveur Node tourne (port 3001 ou api.takkyuoni.com).",
    retryable: true,
  };
  if (error.status === 529) return {
    title: "Claude est surchargé",
    detail: "L'API Anthropic est momentanément saturée. Réessaie dans 30 secondes.",
    retryable: true,
  };
  return { title: "Erreur du Chroniqueur", detail: error.message || "Erreur inconnue.", retryable: true };
}

export async function callClaude({ messages, maxTokens = 800 }) {
  const response = await fetchWithTimeout(CLAUDE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model: "claude-sonnet-4-5", max_tokens: maxTokens, messages }),
  });
  if (!response.ok) {
    const err = new Error(`HTTP ${response.status}`);
    err.status = response.status;
    throw err;
  }
  const data = await response.json();
  if (data.error) throw new Error(data.error.message || data.error);
  const text = data.content?.find(b => b.type === "text")?.text || "";
  if (!text) throw new Error("Réponse vide du serveur");
  return text;
}

export function parseClaudeJSON(text) {
  return JSON.parse(text.replace(/```json|```/g, "").trim());
}

export function useClaude() {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const generate = useCallback(async ({ messages, maxTokens, parseJSON = false }) => {
    setLoading(true);
    setError(null);
    try {
      const text = await callClaude({ messages, maxTokens });
      setLoading(false);
      return parseJSON ? parseClaudeJSON(text) : text;
    } catch(e) {
      setError(getErrorMessage(e));
      setLoading(false);
      return null;
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);
  return { generate, loading, error, clearError };
}

export function ClaudeError({ error, onRetry, onDismiss }) {
  if (!error) return null;
  return (
    <div style={{
      background: "rgba(192,57,43,0.08)", border: "1px solid rgba(192,57,43,0.35)",
      borderLeft: "3px solid var(--blood)", padding: "1rem 1.25rem", marginTop: "0.75rem",
    }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"1rem" }}>
        <div>
          <p style={{ fontSize:".82rem", color:"var(--paper)", marginBottom:"4px", fontFamily:"var(--font-kanji)" }}>
            💀 {error.title}
          </p>
          <p style={{ fontSize:".72rem", color:"var(--ash)", lineHeight:1.7 }}>{error.detail}</p>
        </div>
        <div style={{ display:"flex", gap:"6px", flexShrink:0 }}>
          {error.retryable && onRetry && (
            <button onClick={onRetry} style={{
              background:"none", border:"1px solid var(--border-red)", color:"var(--blood)",
              fontFamily:"var(--font-mono)", fontSize:".6rem", letterSpacing:"2px",
              padding:"5px 10px", cursor:"pointer",
            }}>RÉESSAYER ↺</button>
          )}
          {onDismiss && (
            <button onClick={onDismiss} style={{
              background:"none", border:"1px solid var(--border)", color:"var(--ash)",
              fontFamily:"var(--font-mono)", fontSize:".6rem", padding:"5px 8px", cursor:"pointer",
            }}>✕</button>
          )}
        </div>
      </div>
    </div>
  );
}
